import React from "react";
import {
  useAddConventionToPlayTest,
  useDeletePlayTest,
  useRemoveConventionFromPlayTest,
  useGamePlayTest,
  useGamePlayTestMetaDataEntry,
  useUpdatePlayTest,
  useUpdatePlayTestMetaData,
  useGame,
  useMyProfile,
  useGameSlugs,
} from "@seabrookstudios/pitch2table";
import Card from "@mui/material/Card";
import { useGameIdFromRoute, usePlayTestIdFromRoute, usePlayTestMetaIdFromRoute } from "../games/WithGameIdFromRoute";
import { EmptyDetailCard } from "../core/EmptyDetailCard";
import Box from "@mui/material/Box";
import { useDocumentTitle } from "usehooks-ts";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import CardHeader from "@mui/material/CardHeader";
import Delete from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import { useNavigate } from "react-router-dom";
import {
  ColorPicker,
  ColorArea,
  ColorSlider,
  ColorThumb,
  ColorSwatchPicker,
  SliderOutput,
  Label,
  SliderTrack,
  ColorSwatch,
  ColorSwatchPickerItem,
} from "react-aria-components";
import { Path } from "../Path";
import { EditableListItemNumberWithUndefined, EditableListItemText } from "../core/EditableListItemText";
import Refresh from "@mui/icons-material/Refresh";
import Stack from "@mui/material/Stack";
import { EditableSwitch } from "../core/EditableSwitch";
import { EditableDate } from "../core/EditableDate";
import { AudienceSelect } from "./AudienceSelect";
import { MediumSelect } from "./MediumSelect";
import { RulesVersionSelect } from "../games/publisher-interest/PublisherInterestRulesSentSelect";
import { TopicDisplay } from "../message-board/TopicFromRoute";
import { AddMessageToPlayTest } from "../message-board/AddTopic";
import { MetadataTypeSelect } from "./MetadataTypeSelect";
import Archive from "@mui/icons-material/Archive";
import {
  normalizePlayTestEnumTypes,
  PlayTestMetadataValueTypeLabels,
} from "@seabrookstudios/pitch2table-core";
import MoveToInbox from "@mui/icons-material/MoveToInbox";
import Alert from "@mui/material/Alert";
import { ReadOnlyScoringComponent } from "./Scoring";
import { makeOnSuccess } from "../core/toast";
import Tooltip from "@mui/material/Tooltip";
import { CombinedScoreResultsWithToggle, TrackGameLevelResult } from "./Chart";
import Divider from "@mui/material/Divider";
import { PlayTestPhotos } from "./PlayTestPhotos";
import { CircularProgressSize } from "../theme";
import { patterns } from "@seabrookstudios/pitch2table-core";
import ListItemText from "@mui/material/ListItemText";
import { FullScreenDialog } from "../core/FullScreenDialog";
import { ColorPatternDisplay } from "./ColorPatternDisplay";
import { colorBlindFriendlyColors } from "@seabrookstudios/pitch2table-core";
import { SelectConventionDialogForPlayTest } from "../core/SelectConventionCard";
import { DownloadQrCodeStickerIcon } from "../core/QrCode";
import Feedback from "@mui/icons-material/Feedback";
import { shortUrlPlayTestFeedback } from "../games/model";

/** @type {import("@seabrookstudios/pitch2table-core").PlayTestPattern[]} */
const allPatterns = ["none", ...patterns];

const styles = {
  selected: {
    borderStyle: "solid",
    borderWidth: 4,
    borderColor: "black",
    cursor: "pointer",
    padding: 0,
    margin: 0,
    borderRadius: 8,
  },
  unselected: {
    borderStyle: "solid",
    borderWidth: 4,
    borderColor: "transparent",
    cursor: "pointer",
    padding: 0,
    margin: 0,
    borderRadius: 8,
  },
  colorThumb: {
    border: "2px solid white",
    boxShadow: "0 0 0 1px black, inset 0 0 0 1px black",
    width: 20,
    height: 20,
    borderRadius: "50%",
  },
  colorSlider: {
    display: "grid",
    gridTemplateAreas: `"label output" "track track"`,
    gridTemplateColumns: "1fr auto",
    gap: 4,
    width: "100%",
    top: "50%",
  },
  colorArea: {
    width: "100%",
    height: 192,
    borderRadius: 4,
    flexShrink: 0,
  },
  colorSliderLabel: {
    gridArea: "label",
  },
  colorSliderOutput: {
    gridArea: "output",
  },
  colorSliderTrack: {
    gridArea: "track",
    height: 28,
  },
  colorThumbSlider: {
    top: "50%",
    border: "2px solid white",
    boxShadow: "0 0 0 1px black, inset 0 0 0 1px black",
    width: 20,
    height: 20,
    borderRadius: "50%",
  },
  colorSwatchPicker: {
    display: "flex",
    gap: 8,
  },
  colorSwatchPickerItem: {
    outline: "none",
    borderRadius: 4,
    width: "fit-content",
  },
  colorSwatch: {
    width: 32,
    height: 32,
    borderRadius: 4,
    boxShadow: "inset 0 0 0 1px rgba(0, 0, 0, 0.1)",
  },
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponent} props.metadata
 * @param {(color: string, pattern: import("@seabrookstudios/pitch2table-core").PlayTestPattern) => void} props.onSave
 * @returns {React.ReactNode}
 */
const ColorPickerDialog = ({ metadata, onSave }) => {
  const [selectedColor, setSelectedColor] = React.useState(metadata.colour || "#000000");
  const [selectedPattern, setSelectedPattern] = React.useState(metadata.pattern || "none");

  /**
   * @param {import("react-aria-components").Color} color
   */
  const handleColorChange = (color) => {
    setSelectedColor(color.toString("hex"));
  };

  const handleSave = () => {
    onSave(selectedColor, selectedPattern);
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PlayTestPattern} pattern
   */
  const selectPattern = (pattern) => {
    setSelectedPattern(pattern);
  };

  return (
    <FullScreenDialog
      title="Choose Color"
      actionText="SAVE"
      onSaveHandler={handleSave}
      CustomControl={<ColorPatternDisplay item={metadata} size={40} />}
    >
      <Box p={2}>
        <ColorPicker defaultValue={selectedColor} onChange={handleColorChange}>
          <Stack direction="row" spacing={2} flex={1}>
            <Stack direction="column" spacing={2} flex={1}>
              <ColorArea colorSpace="hsb" xChannel="saturation" yChannel="brightness" style={styles.colorArea}>
                <ColorThumb style={styles.colorThumb} />
              </ColorArea>
              <ColorSlider colorSpace="hsb" channel="hue" style={styles.colorSlider}>
                <Label style={styles.colorSliderLabel} />
                <SliderOutput style={styles.colorSliderOutput} />
                <SliderTrack style={styles.colorSliderTrack}>
                  <ColorThumb style={styles.colorThumbSlider} />
                </SliderTrack>
              </ColorSlider>
              <ColorSwatchPicker style={styles.colorSwatchPicker}>
                {colorBlindFriendlyColors.map((color) => (
                  <ColorSwatchPickerItem style={styles.colorSwatchPickerItem} color={color} key={color}>
                    <ColorSwatch style={styles.colorSwatch} />
                  </ColorSwatchPickerItem>
                ))}
              </ColorSwatchPicker>
            </Stack>
            <Box flex={1}>
              <Stack direction="row" flex={0} flexWrap="wrap">
                {allPatterns.map((pattern, index) => (
                  <Box
                    key={pattern}
                    onClick={() => selectPattern(pattern)}
                    style={selectedPattern === pattern ? styles.selected : styles.unselected}
                  >
                    <ColorPatternDisplay
                      item={{ ...metadata, colour: selectedColor, pattern }}
                      size={90}
                      patternIndex={index}
                    />
                  </Box>
                ))}
              </Stack>
            </Box>
          </Stack>
        </ColorPicker>
      </Box>
    </FullScreenDialog>
  );
};

/**
 * @param {object} props
 * @param {import("@seabrookstudios/types").GameId} props.gameId
 * @param {import("@seabrookstudios/pitch2table-core").PlayTestId} props.playTestId
 */
const PlayTestFeedbackQR = ({ gameId, playTestId }) => {
  const { data: game } = useGame(gameId);
  const { data: gameSlugs = [] } = useGameSlugs(gameId);
  const { data: profile } = useMyProfile();

  if (!game || !profile) {
    return null;
  }

  if (gameSlugs.length === 0) {
    return null;
  }

  const urlFeedback = shortUrlPlayTestFeedback(profile.slug, gameSlugs[0], playTestId);

  return game.feedbackEnabled ? (
    <DownloadQrCodeStickerIcon
      url={urlFeedback}
      filename={`pitch2table-qr-${playTestId}-feedback.png`}
      Icon={Feedback}
      title="Feedback QR"
      subtitle="Use this QR code to collect feedback on this play test."
      cta="Scan QR to give feedback on this play test"
      enabled={game.feedbackEnabled}
    />
  ) : (
    <Tooltip title="Enable feedback to access this QR code.">
      <Box>
        <DownloadQrCodeStickerIcon
          url={urlFeedback}
          filename={`pitch2table-qr-${playTestId}-feedback.png`}
          Icon={Feedback}
          title="Feedback QR"
          subtitle="Use this QR code to collect feedback on this play test."
          cta="Scan QR to give feedback on this play test"
          enabled={game.feedbackEnabled}
        />
      </Box>
    </Tooltip>
  );
};

/**
 * @type {import("@seabrookstudios/pitch2table-core").UpdatePlayTestMarkdown}
 */
const defaultPlayTestRequest = {
  date: undefined,
  duration: undefined,
  rulesVersion: undefined,
  complete: undefined,
  ignore: undefined,
  body: undefined,
  audience: undefined,
  medium: undefined,
  publisherId: undefined,
  outcomes: undefined,
  nonPlayerOutcomes: undefined,
  implementation: "Markdown",
  timerRunning: undefined,
  timerStarted: undefined,
};

export const PlayTestFromRoute = () => {
  const gameId = useGameIdFromRoute();
  const playTestId = usePlayTestIdFromRoute();
  const { data: playTest, refetch, isRefetching } = useGamePlayTest(gameId, playTestId);
  const updatePlayTest = useUpdatePlayTest(gameId, playTestId, makeOnSuccess("Play test updated."));
  const deletePlayTest = useDeletePlayTest(gameId, playTestId, makeOnSuccess("Play test deleted."));
  const addConventionToPlayTest = useAddConventionToPlayTest(gameId, playTestId);
  const removeConventionFromPlayTest = useRemoveConventionFromPlayTest(gameId, playTestId);
  const navigate = useNavigate();

  useDocumentTitle(playTest ? "Play Test" : "Pitch2Table");

  if (!playTestId) {
    return <EmptyDetailCard text="Select a play test from the list on the left." />;
  }

  const onDeleteHandler = async () => {
    await deletePlayTest.mutateAsync();

    await navigate(Path.game(gameId).tests(), { replace: true });
  };

  /**
   * @param {number} duration
   */
  const onEditDuration = async (duration) => {
    await updatePlayTest.mutateAsync({
      ...defaultPlayTestRequest,
      duration,
    });
  };

  /**
   * @param {boolean} ignore
   */
  const onEditIgnore = async (ignore) => {
    await updatePlayTest.mutateAsync({
      ...defaultPlayTestRequest,
      ignore,
    });
  };

  /**
   * @param {boolean} complete
   */
  const onEditComplete = async (complete) => {
    await updatePlayTest.mutateAsync({
      ...defaultPlayTestRequest,
      complete,
    });
  };

  /**
   * @param {number} date
   */
  const onEditDate = async (date) => {
    await updatePlayTest.mutateAsync({
      ...defaultPlayTestRequest,
      date,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PlayTestAudience} audience
   */
  const onEditAudience = async (audience) => {
    await updatePlayTest.mutateAsync({
      ...defaultPlayTestRequest,
      audience,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PlayTestMedium} medium
   */
  const onEditMedium = async (medium) => {
    await updatePlayTest.mutateAsync({
      ...defaultPlayTestRequest,
      medium,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").AttachmentId} rulesVersion
   */
  const onEditRulesVersion = async (rulesVersion) => {
    await updatePlayTest.mutateAsync({
      ...defaultPlayTestRequest,
      rulesVersion,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").ConventionId|null} conventionId
   */
  const onEditConvention = async (conventionId) => {
    if (!playTest) {
      return;
    }

    if (!conventionId && playTest.conventionId) {
      await removeConventionFromPlayTest.mutateAsync({ conventionId: playTest.conventionId });
      return;
    }
    if (!conventionId) {
      return;
    }

    await addConventionToPlayTest.mutateAsync({ conventionId });
  };

  if (!playTest) {
    return (
      <Box>
        <Card elevation={1} square={false}>
          <CircularProgress />
        </Card>
      </Box>
    );
  }

  return (
    <Box>
      <Card elevation={1} square={false}>
        <CardHeader
          action={
            <Stack direction="row">
              <PlayTestFeedbackQR gameId={gameId} playTestId={playTestId} />
              <IconButton onClick={onDeleteHandler}>
                {deletePlayTest.isPending ? <CircularProgress size={CircularProgressSize} /> : <Delete />}
              </IconButton>
              <IconButton onClick={() => refetch()}>
                {isRefetching ? <CircularProgress size={CircularProgressSize} /> : <Refresh />}
              </IconButton>
            </Stack>
          }
        />
        <List>
          <Stack direction="row">
            <ListItem>
              <EditableDate
                isBusy={updatePlayTest.isPending}
                onSave={onEditDate}
                timestamp={playTest.date}
                title="Date of Play Test"
                disableFuture
                popupTitle="date of play test"
              />
            </ListItem>
            <ListItem>
              <EditableListItemNumberWithUndefined
                primary="Duration (minutes)"
                secondary={playTest.duration}
                helperText="How long did the game go for (in minutes)?"
                onSave={onEditDuration}
                isBusy={updatePlayTest.isPending}
              />
            </ListItem>
          </Stack>
          <ListItem>
            <AudienceSelect
              value={playTest.audience}
              onChange={onEditAudience}
              helperText="Broadly speaking, how would you classify the play testers?"
            />
          </ListItem>
          <ListItem>
            <MediumSelect
              value={playTest.medium}
              onChange={onEditMedium}
              helperText="Is this an in-person or virtual play test?"
            />
          </ListItem>
          <ListItem>
            <RulesVersionSelect
              label="Rules Version for Test"
              gameId={gameId}
              value={playTest.rulesVersion}
              onChange={onEditRulesVersion}
              helperText="The rules version used for this play test. Use the comments section below to note impromptu rule changes."
            />
          </ListItem>
          <ListItem>
            <SelectConventionDialogForPlayTest
              value={playTest.conventionId ? [playTest.conventionId] : []}
              onChange={onEditConvention}
              sessionDate={playTest.date}
            />
          </ListItem>
          <Stack direction="row">
            <ListItem>
              <EditableSwitch
                initialValue={playTest.complete}
                title="Completed"
                offLabel="This play test was not completed."
                onLabel="This play test is complete."
                onUpdate={onEditComplete}
              />
            </ListItem>
            <ListItem>
              <EditableSwitch
                initialValue={playTest.ignore}
                title="Include in statistics"
                onLabel="This play test will be included in statistics."
                offLabel="This play test won't be used for statistics."
                onUpdate={onEditIgnore}
              />
            </ListItem>
          </Stack>
        </List>
      </Card>
      <Box pt={1}>
        <Card>
          <ReadOnlyScoringComponent
            outcomes={playTest.outcomes}
            gameId={gameId}
            playTestId={playTestId}
            nonPlayerOutcomes={playTest.nonPlayerOutcomes}
          />
        </Card>
      </Box>
      <Box pt={1}>
        <Card>
          <PlayTestPhotos />
        </Card>
      </Box>
      <Box pt={1}>
        {playTest.topicId ? (
          <TopicDisplay
            gameId={gameId}
            topicId={playTest.topicId}
            hideType
            hideDate
            customTitle="Play Test Notes"
            hideTitle={false}
          />
        ) : (
          <AddMessageToPlayTest gameId={gameId} playTestId={playTestId} />
        )}
      </Box>
    </Box>
  );
};

export const PlayTestMetadataFromRoute = () => {
  const gameId = useGameIdFromRoute();
  const metaId = usePlayTestMetaIdFromRoute();
  const { data: metadata, refetch, isRefetching } = useGamePlayTestMetaDataEntry(gameId, metaId);
  const updatePlayTest = useUpdatePlayTestMetaData(gameId, metaId);

  useDocumentTitle(metadata ? metadata.name : "Pitch2Table");

  if (!metaId) {
    return <EmptyDetailCard text="Select a play test from the list on the left." />;
  }

  const onArchiveHandler = async () => {
    if (!metadata) {
      return;
    }

    await updatePlayTest.mutateAsync({
      name: undefined,
      order: undefined,
      type: undefined,
      archived: !metadata.archived,
      valueType: undefined,
      enumTypes: undefined,
      colour: undefined,
      pattern: undefined,
    });
  };

  /**
   * @param {string} name
   */
  const onEditName = async (name) => {
    await updatePlayTest.mutateAsync({
      name,
      order: undefined,
      type: undefined,
      archived: undefined,
      valueType: undefined,
      enumTypes: undefined,
      colour: undefined,
      pattern: undefined,
    });
  };

  /**
   * @param {import("@seabrookstudios/pitch2table-core").PlayTestMetadataComponentType} type
   */
  const onEditType = async (type) => {
    await updatePlayTest.mutateAsync({
      name: undefined,
      order: undefined,
      type,
      archived: undefined,
      valueType: undefined,
      enumTypes: undefined,
      colour: undefined,
      pattern: undefined,
    });
  };

  /**
   * @param {string} colour
   * @param {import("@seabrookstudios/pitch2table-core").PlayTestPattern} pattern
   */
  const onEditColorAndPattern = async (colour, pattern) => {
    await updatePlayTest.mutateAsync({
      name: undefined,
      order: undefined,
      type: undefined,
      archived: undefined,
      valueType: undefined,
      enumTypes: undefined,
      colour,
      pattern,
    });
  };

  /**
   * @param {string} nextEnumTypes
   */
  const onEditEnumTypes = async (nextEnumTypes) => {
    await updatePlayTest.mutateAsync({
      name: undefined,
      order: undefined,
      type: undefined,
      archived: undefined,
      valueType: undefined,
      enumTypes: normalizePlayTestEnumTypes(nextEnumTypes) || "",
      colour: undefined,
      pattern: undefined,
    });
  };

  if (!metadata) {
    return (
      <Box>
        <Card elevation={1} square={false}>
          <CircularProgress />
        </Card>
      </Box>
    );
  }

  const Icon = metadata.archived ? MoveToInbox : Archive;

  return (
    <Box>
      <Card elevation={1} square={false}>
        <CardHeader
          action={
            <Stack direction="row">
              <Tooltip title={metadata.archived ? "Restore from archive" : "Archive"}>
                <IconButton onClick={onArchiveHandler}>
                  {updatePlayTest.isPending ? <CircularProgress size={CircularProgressSize} /> : <Icon />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Refresh">
                <IconButton onClick={() => refetch()}>
                  {isRefetching ? <CircularProgress size={CircularProgressSize} /> : <Refresh />}
                </IconButton>
              </Tooltip>
            </Stack>
          }
        />
        <List>
          {metadata.archived && (
            <ListItem>
              <Alert severity="info">
                Archived! This tracking variable can no longer be added to play tests. It will remain present in all
                play tests that it is currently recorded in.
              </Alert>
            </ListItem>
          )}
          <ListItem>
            <EditableListItemText
              primary="Name"
              secondary={metadata.name}
              onSave={onEditName}
              isBusy={updatePlayTest.isPending}
              helperText="The name of the tracking variable e.g. score, trade action, etc"
            />
          </ListItem>
          <ListItem>
            <MetadataTypeSelect
              value={metadata.type}
              onChange={onEditType}
              helperText="How is this value tracked, per game, per player, part of the score or is the score?"
            />
          </ListItem>
          <ListItem>
            <ListItemText
              primary="Value Type"
              secondary={`${
                PlayTestMetadataValueTypeLabels[metadata.valueType || "NUMERIC"]
              }. Value type is set when the tracking variable is created. Archive this one and create another if you need a different type.`}
            />
          </ListItem>
          {metadata.valueType === "ENUM" && (
            <ListItem>
              <EditableListItemText
                primary="Choices"
                secondary={metadata.enumTypes || ""}
                onSave={onEditEnumTypes}
                isBusy={updatePlayTest.isPending}
                helperText="Comma-separated choices, e.g. Swimmer Won, On Boat Won, Captain Won"
              />
            </ListItem>
          )}
          {metadata.type === "PART_OF_SCORE" && (
            <ListItem>
              <ListItemText primary="Colour & Pattern" />
              <ColorPickerDialog metadata={metadata} onSave={onEditColorAndPattern} />
            </ListItem>
          )}
        </List>
        <Divider />
        {metadata.type === "SCORE" && <CombinedScoreResultsWithToggle gameId={gameId} metaId={metadata.id} />}
        {metadata.type === "PER_GAME" && (
          <TrackGameLevelResult
            gameId={gameId}
            metaId={metadata.id}
            valueType={metadata.valueType}
            enumTypes={metadata.enumTypes}
          />
        )}
        {metadata.type === "PART_OF_SCORE" && <CombinedScoreResultsWithToggle gameId={gameId} metaId={metadata.id} />}
        {metadata.type === "PER_PLAYER" && (
          <CombinedScoreResultsWithToggle
            gameId={gameId}
            metaId={metadata.id}
            valueType={metadata.valueType}
            enumTypes={metadata.enumTypes}
          />
        )}
      </Card>
    </Box>
  );
};
